import { p as proxyCustomElement, H, d as createEvent, h, c as Host } from './p-23afa4c1.js';
import { t as tippy, f as followCursor, s as sticky } from './p-5b08239b.js';
import { i as isNodeEnvironment } from './p-2f624564.js';
import { C as CSSTransition } from './p-356d8deb.js';
import { n as nextTick } from './p-4f3570ee.js';
import { d as defineCustomElement$3 } from './p-d8e88252.js';
import { d as defineCustomElement$2 } from './p-0f8558ea.js';
import { b as bem } from './p-a05fe53c.js';
import { r as resolveAppearance } from './p-d1ae9dd0.js';
import { h as hasNamedSlot } from './p-bde29a8a.js';

class EventEmitter {
    constructor() {
        if (!isNodeEnvironment()) {
            // eslint-disable-next-line no-restricted-globals
            this.eventTarget = document.createDocumentFragment();
        }
    }
    on(eventName, callback) {
        var _a;
        (_a = this.eventTarget) === null || _a === void 0 ? void 0 : _a.addEventListener(eventName, callback);
    }
    off(eventName, callback) {
        var _a;
        (_a = this.eventTarget) === null || _a === void 0 ? void 0 : _a.removeEventListener(eventName, callback);
    }
    emit(eventName, payload) {
        var _a;
        const event = new CustomEvent(eventName, payload);
        (_a = this.eventTarget) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event);
    }
}

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * isNil(null)
 * // => true
 *
 * isNil(void 0)
 * // => true
 *
 * isNil(NaN)
 * // => false
 *
 * isNil(undefined)
 * // => true
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
const isNil = (value) => {
    return value == null;
};

/**
 * Tooltip Class
 *
 * Instantiate a tooltip element
 *
 * @method create(options: TooltipOptions): void
 * @method show(): void
 * @method hide(): Promise<void>
 *
 * @emit show(void)
 * @emit hide(void)
 */
const defaultDuration = 300;
const defaultOffset = 8;
class Tooltip {
    constructor() {
        this.emitter = new EventEmitter();
        this.placementMap = {
            left: 'before',
            right: 'after',
            bottom: 'below',
            top: 'above',
        };
        this.cssTransition = new CSSTransition();
    }
    create({ target, content, appendTo, interactive, placement, showOnCreate, follow, sticky: sticky$1, offset = defaultOffset, enterDuration = defaultDuration, leaveDuration = defaultDuration, trigger, }) {
        this.target = target;
        this.leaveDuration = leaveDuration;
        this.enterDuration = enterDuration;
        this.element = content;
        this.element.style.display = 'block';
        content.remove();
        this.tooltipInstance = tippy(this.target, {
            appendTo,
            hideOnClick: false,
            interactive,
            offset: [0, offset],
            placement: this.cosmosToPopperPlacement(placement),
            showOnCreate: showOnCreate || false,
            touch: false,
            delay: [null, this.leaveDuration],
            trigger: trigger || undefined,
            sticky: sticky$1,
            plugins: [followCursor, sticky],
            onMount: ({ popperInstance, popper }) => {
                var _a;
                // The actual placement can be derived from poppers state.
                // If the `follow` option is enabled, we set the placement to `follow`
                // to change the enter animation to a simple fade-in
                const actualPlacement = follow ? 'follow' : popperInstance === null || popperInstance === void 0 ? void 0 : popperInstance.state.placement;
                (_a = popper.firstElementChild) === null || _a === void 0 ? void 0 : _a.setAttribute('data-cosmos-tooltip-placement', this.popperToCosmosPlacement(actualPlacement));
                this.cssTransition.enter(popper.firstElementChild, this.enterDuration);
            },
            onShow: () => {
                this.onShowHandler();
            },
            onUntrigger: async () => {
                await this.onHideHandler();
            },
            render: () => {
                return {
                    popper: this.element,
                };
            },
        });
        if (follow) {
            requestAnimationFrame(() => {
                var _a;
                if (this.tooltipInstance && typeof this.tooltipInstance.setProps === 'function') {
                    (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps({ followCursor: this.getTippyFollowDirection(placement) });
                }
            });
        }
    }
    on(eventName, callback) {
        this.emitter.on(eventName, callback);
    }
    off(eventName, callback) {
        this.emitter.off(eventName, callback);
    }
    show() {
        var _a, _b, _c;
        if ((_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.unmount) {
            (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.unmount();
        }
        (_c = this.tooltipInstance) === null || _c === void 0 ? void 0 : _c.show();
    }
    async hide() {
        await this.onHideHandler();
        this.tooltipInstance.hide();
    }
    async update() {
        var _a;
        if (!this.isVisible()) {
            return;
        }
        await nextTick();
        await ((_a = this.tooltipInstance.popperInstance) === null || _a === void 0 ? void 0 : _a.update());
    }
    isVisible() {
        return this.tooltipInstance.state.isVisible;
    }
    setProps(props) {
        var _a;
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps(props);
    }
    destroy() {
        this.tooltipInstance.destroy();
    }
    popperToCosmosPlacement(placement) {
        return this.placementMap[placement];
    }
    cosmosToPopperPlacement(cosmosPlacement) {
        const popperPlacement = Object.keys(this.placementMap).find((placement) => this.placementMap[placement] === cosmosPlacement);
        return popperPlacement || 'top';
    }
    getTippyFollowDirection(placement) {
        return placement === 'above' || placement === 'below' ? 'horizontal' : 'vertical';
    }
    onShowHandler() {
        this.emitter.emit('show');
        this.target.setAttribute('aria-expanded', 'true');
    }
    onHideHandler() {
        this.emitter.emit('hide');
        return this.triggerCssLeaveTransition();
    }
    triggerCssLeaveTransition() {
        return this.cssTransition.leave(this.element.firstElementChild, this.leaveDuration);
    }
}

const cosmosTooltipCss = "@supports (font-variation-settings: normal){::slotted(strong),::slotted(b){font-variation-settings:\"opsz\" 12, \"wght\" 700}}.tippy-mount{display:inline-block;height:0;width:0}.tippy-element{display:none;z-index:var(--cosmos-tooltip-z-index, 9999) !important}.cosmos-tooltip{--cosmos-text-color:var(--cosmos-tooltip-color);--cosmos-text-font-family:var(--cosmos-tooltip-font-family);--cosmos-text-font-size:var(--cosmos-tooltip-font-size);--cosmos-text-font-style:var(--cosmos-tooltip-font-style);--cosmos-text-font-weight:var(--cosmos-tooltip-font-weight);--cosmos-text-letter-spacing:var(--cosmos-tooltip-letter-spacing);--cosmos-text-line-height:var(--cosmos-tooltip-line-height);--cosmos-text-text-transform:var(--cosmos-tooltip-text-transform);background-color:var(--cosmos-tooltip-background, #FFFFFF);border-color:var(--cosmos-tooltip-border-color, transparent);border-radius:var(--cosmos-tooltip-border-radius, 6px);border-style:solid;border-width:var(--cosmos-tooltip-border-width, 0);-webkit-box-shadow:var(--cosmos-tooltip-box-shadow, 0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431));box-shadow:var(--cosmos-tooltip-box-shadow, 0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431));max-width:calc(288rem / var(--cosmos-base-font-size, 16));padding:var(--cosmos-tooltip-padding, 4px 8px)}.cosmos-tooltip--spacing-loose{padding:var(--cosmos-tooltip-padding, 8px 12px)}.cosmos-tooltip--appearance-dark{background-color:var(--cosmos-tooltip-background, #00162B)}.cosmos-tooltip.enter{opacity:0}.cosmos-tooltip[data-cosmos-tooltip-placement=above].enter{-webkit-transform:translate(0, 8px);transform:translate(0, 8px)}.cosmos-tooltip[data-cosmos-tooltip-placement=below].enter{-webkit-transform:translate(0, -8px);transform:translate(0, -8px)}.cosmos-tooltip[data-cosmos-tooltip-placement=before].enter{-webkit-transform:translate(8px, 0);transform:translate(8px, 0)}.cosmos-tooltip[data-cosmos-tooltip-placement=after].enter{-webkit-transform:translate(-8px, 0);transform:translate(-8px, 0)}.cosmos-tooltip[data-cosmos-tooltip-placement].enter-active{opacity:1;-webkit-transform:translate(0, 0);transform:translate(0, 0);-webkit-transition:0.15s cubic-bezier(0.33, 1, 0.68, 1);transition:0.15s cubic-bezier(0.33, 1, 0.68, 1)}.cosmos-tooltip.leave{opacity:1}.cosmos-tooltip.leave-active{opacity:0;-webkit-transition:0.15s cubic-bezier(0, 0.32, 0.32, 1);transition:0.15s cubic-bezier(0, 0.32, 0.32, 1)}.cosmos-tooltip--empty{display:none}";
const CosmosTooltipStyle0 = cosmosTooltipCss;

const generateClassNames = bem('cosmos-tooltip');
const CosmosTooltip$1 = /*@__PURE__*/ proxyCustomElement(class CosmosTooltip extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.hideEvent = createEvent(this, "hide", 7);
        this.showEvent = createEvent(this, "show", 7);
        this.tippyElement = undefined;
        this.tippyMount = undefined;
        this.tooltipInstance = undefined;
        this.showHandler = () => {
            this.showEvent.emit();
        };
        this.hideHandler = () => {
            this.hideEvent.emit();
        };
        this.appearance = undefined;
        this.content = '';
        this.controlled = false;
        this.follow = false;
        this.offset = 8;
        this.placement = 'above';
        this.spacing = 'tight';
        this.unreachable = false;
        this.visible = false;
        this.computedAppearance = 'light';
        this.hasContent = false;
    }
    /**
     * Triggers an update of the tooltip positioning.
     */
    async update() {
        var _a;
        if (!this.tooltipInstance) {
            return;
        }
        await ((_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.update());
    }
    handleAppearanceChange(appearance) {
        this.computedAppearance = appearance;
    }
    componentWillLoad() {
        this.instantiateTooltip();
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'dark', light: 'light' },
            callback: this.handleAppearanceChange.bind(this),
        });
    }
    componentDidLoad() {
        this.createTooltip();
        this.createObserver();
    }
    disconnectedCallback() {
        var _a, _b;
        this.removeTooltipListeners();
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
    }
    handleShowChange() {
        var _a, _b, _c, _d;
        /* Setting touch to true in order to make sure that the tooltip will also be shown on mobile/touch devices (tippy checks for this before showing) */
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps({ touch: true });
        if (this.visible) {
            (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.show();
        }
        else {
            (_c = this.tooltipInstance) === null || _c === void 0 ? void 0 : _c.hide();
        }
        (_d = this.tooltipInstance) === null || _d === void 0 ? void 0 : _d.setProps({ touch: false });
    }
    createObserver() {
        const contentSlot = this.element.querySelector('[slot="content"]');
        if (!contentSlot) {
            return;
        }
        const mutationObserver = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.update();
                }
            });
        });
        mutationObserver.observe(contentSlot, {
            childList: true,
            subtree: true,
        });
        this.observer = mutationObserver;
    }
    instantiateTooltip() {
        if (isNodeEnvironment()) {
            return;
        }
        this.tooltipInstance = new Tooltip();
    }
    createTooltip() {
        var _a;
        if (!this.tooltipInstance) {
            return;
        }
        this.tooltipOptions = {
            target: this.getTooltipTarget(),
            appendTo: this.tippyMount,
            showOnCreate: this.visible,
            placement: this.placement || 'above',
            content: this.tippyElement,
            follow: this.normalizedFollowValue,
            sticky: this.follow === 'target',
            interactive: !this.unreachable,
            offset: this.offset,
            enterDuration: 150,
            leaveDuration: 150,
            trigger: this.controlled ? 'manual' : undefined,
        };
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.create(this.tooltipOptions);
        this.addTooltipListeners();
    }
    addTooltipListeners() {
        var _a, _b;
        if (!this.tooltipInstance) {
            return;
        }
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.on('show', this.showHandler);
        (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.on('hide', this.hideHandler);
    }
    removeTooltipListeners() {
        var _a, _b;
        if (!this.tooltipInstance) {
            return;
        }
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.off('show', this.showHandler);
        (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.off('hide', this.hideHandler);
    }
    getTooltipTarget() {
        return Array.from(this.element.children).find((el) => el.tagName.toLowerCase() !== 'style' && el.getAttribute('slot') !== 'content');
    }
    getContent() {
        return (this.content && (h("cosmos-text", { size: "x-small", weight: "medium", exportparts: "base: text" }, this.content)));
    }
    determineIfTooltipHasContent() {
        const hasContentSlot = hasNamedSlot(this.element, 'content');
        this.hasContent = (!isNil(this.content) && this.content !== '') || hasContentSlot;
    }
    componentWillRender() {
        this.normalizedFollowValue = this.normalizeFollowValue();
        this.determineIfTooltipHasContent();
    }
    /**
     * Since the follow value can be a string or a boolean, we need to normalize it
     * and split it since in tippy follow and sticky are two different options.
     */
    normalizeFollowValue() {
        if (this.follow === true || this.follow === '') {
            return true;
        }
        return this.follow === 'cursor';
    }
    async handleContentChange() {
        await this.update();
    }
    render() {
        return (h(Host, { key: 'efa92706e25aa50cd5836d03422b6b42a4b0b90e' }, h("div", { key: '5083be0aa0952f36d8afdc541af03609d0cf0c17', class: "tippy-mount", ref: (element) => (this.tippyMount = element) }), h("div", { key: '1ffb383297faea3952e3d08c0471076359678b30', class: "tippy-element", ref: (element) => (this.tippyElement = element) }, h("div", { key: 'f26b1d2edb04e3634ad525c750c5b09afb5f1111', class: generateClassNames({
                spacing: this.spacing,
                appearance: this.computedAppearance,
                unreachable: this.unreachable,
                empty: !this.hasContent,
            }), part: "base" }, h("cosmos-mode", { key: '45bbbe4e0375c6599ee02b1e19c57335a6d39cd2', mode: this.computedAppearance }, h("span", { key: 'f80105f75ea4282a762371c330b6f4b481bfdea3' }, h("slot", { key: 'c24732049285d5512d74e38febc06d9fd2e78c66', name: "content", onSlotchange: () => this.determineIfTooltipHasContent() }, this.getContent()))))), h("slot", { key: 'f2df074a923be0fdcad4312fcfb9b856faf772f2' })));
    }
    get element() { return this; }
    static get watchers() { return {
        "appearance": ["handleAppearanceChange"],
        "visible": ["handleShowChange"],
        "content": ["handleContentChange"]
    }; }
    static get style() { return CosmosTooltipStyle0; }
}, [1, "cosmos-tooltip", {
        "appearance": [513],
        "content": [513],
        "controlled": [516],
        "follow": [520],
        "offset": [514],
        "placement": [513],
        "spacing": [513],
        "unreachable": [516],
        "visible": [1540],
        "computedAppearance": [32],
        "hasContent": [32],
        "update": [64]
    }, undefined, {
        "appearance": ["handleAppearanceChange"],
        "visible": ["handleShowChange"],
        "content": ["handleContentChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-tooltip", "cosmos-mode", "cosmos-text"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-tooltip":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosTooltip$1);
            }
            break;
        case "cosmos-mode":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "cosmos-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const CosmosTooltip = CosmosTooltip$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosTooltip, defineCustomElement };
